// ─── Agent Org Chart Taxonomy ────────────────────────────────────────────────
//
// Mirrors LIVING_SPEC section 6 (DEC-021, 6 May 2026).
//
//                         MARK (Founder)
//                        /              \
//                   ZEUS                 ATHENA
//                 Council CEO           Guardian COO
//                  (Business)          (Fleet Operations)
//
// This file is the single source of truth for how the dashboard renders the
// org chart. The API only knows three coarse tiers (council | guardian |
// infrastructure); the executive grouping and phase status are layered on
// here.
//
// When the LIVING_SPEC changes, update this file — not the API.

export type ExecutiveBranch = 'council' | 'guardian'
export type AgentPhase = 'live' | 'phase-2' | 'phase-3-4' | 'infrastructure'

export interface OrgAgent {
  /** API skill_name. Used to look up live data from /api/v1/agents. */
  skillName: string
  /** Greek pantheon display name. */
  displayName: string
  /** Short role description shown under the name. */
  role: string
  /** Anthropic model used (display string). */
  model: string
  branch: ExecutiveBranch
  /** Sub-group within the executive branch. */
  group: string
  phase: AgentPhase
  /** Hex accent used for the card's left rail and group identity. */
  accent: string
  emoji: string
}

export interface OrgGroup {
  branch: ExecutiveBranch
  /** Group key — used for React keys and section anchors. */
  key: string
  /** Group label shown in the UI (uppercased). */
  label: string
  /** One-line description under the group label. */
  blurb: string
  phase: AgentPhase
  agents: OrgAgent[]
}

export interface OrgBranch {
  branch: ExecutiveBranch
  /** Executive display name (Zeus / Athena). */
  executive: string
  /** Title shown under the executive name. */
  title: string
  /** Branch description. */
  blurb: string
  /** Runtime that owns this branch. */
  runtime: string
  /** Accent color for the branch identity. */
  accent: string
  /** Total agent count across groups (excluding planned-only counts). */
  totalAgents: number
  groups: OrgGroup[]
}

// ─── Council (Zeus, CEO) ─────────────────────────────────────────────────────

const COUNCIL_AGENTS: OrgAgent[] = [
  // Business Intel
  { skillName: 'finance-ops',     displayName: 'Plutus',     role: 'Finance Ops',        model: 'Haiku 4',  branch: 'council', group: 'business-intel',     phase: 'live', accent: '#86efac', emoji: '💎' },
  { skillName: 'sales-pipeline',  displayName: 'Artemis',    role: 'Sales Pipeline',     model: 'Sonnet 4.6', branch: 'council', group: 'business-intel',     phase: 'live', accent: '#22c55e', emoji: '🏹' },
  { skillName: 'product-intel',   displayName: 'Prometheus', role: 'Product Intel',      model: 'Sonnet 4.6', branch: 'council', group: 'business-intel',     phase: 'live', accent: '#8b5cf6', emoji: '🔥' },
  { skillName: 'data-analytics',  displayName: 'Demeter',    role: 'Data & Analytics',   model: 'Haiku 4.5', branch: 'council', group: 'business-intel',     phase: 'live', accent: '#facc15', emoji: '🌾' },
  // Platform Services
  { skillName: 'security-infra',  displayName: 'Kratos',     role: 'Security & Ops',     model: 'Sonnet 4.6', branch: 'council', group: 'platform-services',  phase: 'live', accent: '#f59e0b', emoji: '⚡' },
  { skillName: 'dev-ops',         displayName: 'Asclepius',  role: 'Dev Ops',            model: 'Haiku 4',  branch: 'council', group: 'platform-services',  phase: 'live', accent: '#ec4899', emoji: '⚖️' },
  { skillName: 'qa-testing',      displayName: 'Arete',      role: 'QA & Testing',       model: 'Sonnet 4.6', branch: 'council', group: 'platform-services',  phase: 'live', accent: '#4ade80', emoji: '✅' },
  // Customer Lifecycle
  { skillName: 'customer-success', displayName: 'Hera',      role: 'Customer Success',   model: 'Haiku 4',  branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#eab308', emoji: '👑' },
  { skillName: 'content-engine',  displayName: 'Clio',       role: 'Content & SEO',      model: 'Sonnet 4.6', branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#a855f7', emoji: '✍️' },
  { skillName: 'content-marketing', displayName: 'Apollo',   role: 'Content & Marketing', model: 'Sonnet 4.6', branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#fcd34d', emoji: '🎵' },
  { skillName: 'social-media',    displayName: 'Helios',     role: 'Social Media & Community', model: 'Sonnet 4.6', branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#f97316', emoji: '☀️' },
  { skillName: 'communications-pr', displayName: 'Calliope', role: 'Communications & PR', model: 'Sonnet 4.6', branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#e879f9', emoji: '📣' },
  { skillName: 'market-intel',    displayName: 'Mnemosyne',  role: 'Market Intel',       model: 'Sonnet 4.6', branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#3b82f6', emoji: '🐦' },
  { skillName: 'eu-jurisdiction', displayName: 'Themis',     role: 'EU Jurisdiction Intel', model: 'Sonnet 4.6', branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#0ea5e9', emoji: '🏛️' },
  { skillName: 'driver-comms',    displayName: 'Hermes',     role: 'Driver Communications', model: 'Haiku 4.5 / Sonnet 4.6', branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#38bdf8', emoji: '📨' },
  // Partners
  { skillName: 'partnerships',    displayName: 'Aether',     role: 'Partnerships',       model: 'Haiku 4',  branch: 'council', group: 'partners',           phase: 'live', accent: '#10b981', emoji: '🌈' },
  { skillName: 'integration-hub', displayName: 'Hecate',     role: 'Integration Hub',    model: 'Sonnet 4.6', branch: 'council', group: 'partners',           phase: 'live', accent: '#06b6d4', emoji: '🔗' },
  // Knowledge
  { skillName: 'knowledge-base',  displayName: 'Hestia',     role: 'Knowledge Base',     model: 'Haiku 4.5', branch: 'council', group: 'knowledge',          phase: 'live', accent: '#f4a261', emoji: '📚' },
  // Lead Generation & SEO
  { skillName: 'lead-gen-seo',    displayName: 'Tyche',      role: 'Lead Generation & SEO', model: 'Sonnet 4.6', branch: 'council', group: 'customer-lifecycle', phase: 'live', accent: '#3aa676', emoji: '🍀' },
  // EU Connectors — cross-border registry integrations (partner surface)
  { skillName: 'eu-connectors',   displayName: 'Janus',      role: 'EU Connectors',      model: 'Haiku 4.5', branch: 'council', group: 'partners',           phase: 'live', accent: '#14b8a6', emoji: '🚪' },
]

const COUNCIL_GROUPS: OrgGroup[] = [
  {
    branch: 'council',
    key: 'business-intel',
    label: 'Business Intel',
    blurb: 'Revenue, pipeline, product signal',
    phase: 'live',
    agents: COUNCIL_AGENTS.filter((a) => a.group === 'business-intel'),
  },
  {
    branch: 'council',
    key: 'platform-services',
    label: 'Platform Services',
    blurb: 'Security posture, dev ops, runtime health, QA',
    phase: 'live',
    agents: COUNCIL_AGENTS.filter((a) => a.group === 'platform-services'),
  },
  {
    branch: 'council',
    key: 'customer-lifecycle',
    label: 'Customer Lifecycle',
    blurb: 'Acquisition, success, narrative',
    phase: 'live',
    agents: COUNCIL_AGENTS.filter((a) => a.group === 'customer-lifecycle'),
  },
  {
    branch: 'council',
    key: 'partners',
    label: 'Partners',
    blurb: 'Partner pipeline and integration health',
    phase: 'live',
    agents: COUNCIL_AGENTS.filter((a) => a.group === 'partners'),
  },
  {
    branch: 'council',
    key: 'knowledge',
    label: 'Knowledge',
    blurb: 'Internal wiki — product, regulation, process, FAQ',
    phase: 'live',
    agents: COUNCIL_AGENTS.filter((a) => a.group === 'knowledge'),
  },
]

// ─── Guardians (Athena, COO) ─────────────────────────────────────────────────

const GUARDIAN_AGENTS: OrgAgent[] = [
  // Phase A — LIVE (9)
  { skillName: 'compliance-officer',     displayName: 'Dike',       role: 'Compliance Officer',           model: 'Haiku 4.5',           branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#22d3ee', emoji: '⚖️' },
  { skillName: 'maintenance-scheduling', displayName: 'Hephaestus', role: 'Maintenance Scheduling',       model: 'Haiku 4.5',           branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#f97316', emoji: '🔧' },
  { skillName: 'defect-triage',          displayName: 'Argus',      role: 'Defect Triage',                model: 'Sonnet 4.6',            branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#fb7185', emoji: '👁️' },
  { skillName: 'workshop-ops',           displayName: 'Daedalus',   role: 'Workshop Ops',                 model: 'Haiku 4.5',           branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#fbbf24', emoji: '🏭' },
  { skillName: 'fleet-finance',          displayName: 'Coeus',      role: 'Fleet Financial Intelligence', model: 'Haiku 4.5 / Sonnet 4.6', branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#34d399', emoji: '📊' },
  { skillName: 'regulatory-monitoring',  displayName: 'Aletheia',   role: 'Regulatory Monitoring',        model: 'Sonnet 4.6',            branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#60a5fa', emoji: '📜' },
  { skillName: 'document-generation',    displayName: 'Cadmus',     role: 'Document Generation',          model: 'Haiku 4.5',           branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#c084fc', emoji: '📝' },
  { skillName: 'iris',                   displayName: 'Iris',       role: 'Email & Driver Comms',         model: 'Haiku 4.5',           branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#f472b6', emoji: '📧' },
  { skillName: 'parts-procurement',      displayName: 'Poros',      role: 'Parts Procurement',            model: 'Haiku 4.5 / Sonnet 4.6', branch: 'guardian', group: 'phase-a', phase: 'live', accent: '#a3e635', emoji: '🔩' },

  // Phase 2 — Specced (3)
  // Hecate (Integration Hub) was promoted out of this specced list and built as
  // a LIVE Council agent under Zeus (skillName 'integration-hub', TMA-101) —
  // see COUNCIL_AGENTS above. Kept out of here to avoid a duplicate display
  // name in the org lookup. The product-repo Hecate (VOL browser automation)
  // remains a separate future build.
  { skillName: 'nemesis',                displayName: 'Nemesis',    role: 'Cross-Agent Anomaly Detection', model: 'Haiku 4.5 / Sonnet 4.6', branch: 'guardian', group: 'phase-2', phase: 'phase-2', accent: '#94a3b8', emoji: '⚖' },
  { skillName: 'chiron',                 displayName: 'Chiron',     role: 'Driver Training & CPC',         model: 'Haiku 4.5 / Sonnet 4.6', branch: 'guardian', group: 'phase-2', phase: 'phase-2', accent: '#94a3b8', emoji: '🎓' },
  { skillName: 'proteus',                displayName: 'Proteus',    role: 'Data Migration',                model: 'Haiku 4.5 / Sonnet 4.6', branch: 'guardian', group: 'phase-2', phase: 'phase-2', accent: '#94a3b8', emoji: '🌊' },

  // Phase 3-4 — Specced (0)
  // Arete (QA, Testing & Optimisation) was promoted out of this specced list
  // and built as a LIVE Council agent under Zeus (skillName 'qa-testing',
  // TMA-96) — see COUNCIL_AGENTS above. Kept out of here to avoid a duplicate
  // skillName in the org lookup.
  // Hermes (Driver Communications) was likewise promoted and built as a LIVE
  // Council agent under Zeus (skillName 'driver-comms', TMA-22) — see
  // COUNCIL_AGENTS above. The product-repo Iris (email + driver comms delivery)
  // remains the separate Guardian-side delivery surface.
]

const INFRASTRUCTURE_AGENTS: OrgAgent[] = [
  { skillName: 'fleet-intelligence-hub', displayName: 'Fleet Intelligence Hub', role: 'Event routing & priority resolution', model: 'TypeScript state machine', branch: 'guardian', group: 'infrastructure', phase: 'infrastructure', accent: '#22d3ee', emoji: '🔗' },
  { skillName: 'compliance-kpi-engine',  displayName: 'Compliance KPI Engine',  role: 'DVSA Earned Recognition KPIs',        model: 'TypeScript service',      branch: 'guardian', group: 'infrastructure', phase: 'infrastructure', accent: '#22d3ee', emoji: '📈' },
]

const GUARDIAN_GROUPS: OrgGroup[] = [
  {
    branch: 'guardian',
    key: 'phase-a',
    label: 'Phase A · Live',
    blurb: '9 Guardians shipping today — Inngest',
    phase: 'live',
    agents: GUARDIAN_AGENTS.filter((a) => a.group === 'phase-a'),
  },
  {
    branch: 'guardian',
    key: 'phase-2',
    label: 'Phase 2 · Specced',
    blurb: 'Specced next-up — not yet built',
    phase: 'phase-2',
    agents: GUARDIAN_AGENTS.filter((a) => a.group === 'phase-2'),
  },
  // Phase 3-4 group removed — its sole specced agent (Hermes) was promoted to
  // a LIVE Council agent (skillName 'driver-comms', TMA-22). Re-add this group
  // if a new Phase 3-4 Guardian is specced.
  {
    branch: 'guardian',
    key: 'infrastructure',
    label: 'Infrastructure',
    blurb: 'Under Athena · non-LLM services',
    phase: 'infrastructure',
    agents: INFRASTRUCTURE_AGENTS,
  },
]

// ─── Branches ────────────────────────────────────────────────────────────────

export const COUNCIL_BRANCH: OrgBranch = {
  branch: 'council',
  executive: 'Zeus',
  title: 'Council CEO · Business',
  blurb: 'Owns business briefing and business decision queue',
  runtime: 'Athena Runtime',
  accent: '#fbbf24',
  totalAgents: COUNCIL_AGENTS.length,
  groups: COUNCIL_GROUPS,
}

export const GUARDIAN_BRANCH: OrgBranch = {
  branch: 'guardian',
  executive: 'Athena',
  title: 'Guardian COO · Fleet Operations',
  blurb: 'Owns operations briefing and operations decision queue',
  runtime: 'Inngest',
  accent: '#22d3ee',
  totalAgents: GUARDIAN_AGENTS.length + INFRASTRUCTURE_AGENTS.length,
  groups: GUARDIAN_GROUPS,
}

export const ORG_BRANCHES: OrgBranch[] = [COUNCIL_BRANCH, GUARDIAN_BRANCH]

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ALL_ORG_AGENTS: OrgAgent[] = [
  ...COUNCIL_AGENTS,
  ...GUARDIAN_AGENTS,
  ...INFRASTRUCTURE_AGENTS,
]

const BY_SKILL: Record<string, OrgAgent> = ALL_ORG_AGENTS.reduce<Record<string, OrgAgent>>(
  (acc, a) => {
    acc[a.skillName] = a
    return acc
  },
  {},
)

export function findOrgAgent(skillName: string | null | undefined): OrgAgent | undefined {
  if (!skillName) return undefined
  return BY_SKILL[skillName]
}

export function getPhaseLabel(phase: AgentPhase): string {
  switch (phase) {
    case 'live':           return 'LIVE'
    case 'phase-2':        return 'SPECCED'
    case 'phase-3-4':      return 'SPECCED'
    case 'infrastructure': return 'SERVICE'
  }
}

/**
 * The shipping roster: every agent that is live or a running infrastructure
 * service (excludes specced phase-2 / phase-3-4 agents). This is the set the
 * at-a-glance status grid renders — Council first, then Guardians, in branch
 * order. ~20 agents today.
 */
export function getLiveOrgAgents(): OrgAgent[] {
  return ALL_ORG_AGENTS.filter(
    (a) => a.phase === 'live' || a.phase === 'infrastructure',
  )
}
